import { motion } from "framer-motion";
import { HERO, IMAGES } from "@/data/content";
import { CTAButton } from "./ui";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

export default function Hero() {
  return (
    <section id="top" className="relative min-h-[100svh] flex items-end overflow-hidden bg-brand-graphite grain-overlay">
      {/* Background image with parallax */}
      <motion.div
        aria-hidden
        initial={{ scale: 1.12 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
        className="absolute inset-0"
      >
        <img
          src={IMAGES.heroVineyard}
          alt="Vigneto italiano contemporaneo al tramonto"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-brand-graphite via-brand-graphite/55 to-brand-bordeaux/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-brand-bordeaux/50 to-transparent" />
      </motion.div>

      {/* Eyebrow top */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.15 }}
        className="absolute top-24 sm:top-28 left-4 sm:left-8 lg:left-12"
      >
        <div className="flex items-center gap-3">
          <span className="h-px w-10 bg-brand-gold" />
          <span className="text-xs uppercase tracking-editorial font-semibold text-brand-gold">
            {HERO.eyebrow}
          </span>
        </div>
      </motion.div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pb-16 sm:pb-20 lg:pb-24">
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h1 className="font-serif text-brand-ivory tracking-tight leading-[0.98] text-[13vw] sm:text-6xl lg:text-7xl xl:text-8xl">
              <span className="block overflow-hidden">
                <motion.span
                  className="block text-brand-ivory/70 italic"
                  variants={lineVariant}
                  custom={0}
                  initial="hidden"
                  animate="show"
                >
                  {HERO.headlinePre}
                </motion.span>
              </span>
              {HERO.headlineLines.map((line, i) => (
                <span key={line} className="block overflow-hidden">
                  <motion.span
                    className="block"
                    variants={lineVariant}
                    custom={i + 1}
                    initial="hidden"
                    animate="show"
                  >
                    {line}
                  </motion.span>
                </span>
              ))}
            </h1>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="lg:col-span-4"
          >
            <p className="text-brand-ivory/85 text-base leading-relaxed border-l border-brand-gold/50 pl-5">
              {HERO.body}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <CTAButton href="#lead" data-testid="hero-cta-primary">
                {HERO.ctaPrimary}
              </CTAButton>
              <CTAButton href="#soluzioni" variant="outline" dark data-testid="hero-cta-secondary">
                {HERO.ctaSecondary}
              </CTAButton>
            </div>
            <p className="mt-5 text-xs text-brand-ivory/55 leading-relaxed max-w-sm">
              {HERO.microcopy}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
